/**
 * Leadership Panel — allows human team leaders to issue commands to bot teammates.
 * DOM-based UI component with gold/amber theme matching comms log.
 */

import type { GameState, PlayerState, BotCommandType, WeaponKey, GhostMarker, Vec3 } from '../types';
import { WEAPONS, WEAPON_KEYS } from '../constants';
import { canIssueCommands, getBotTeammates, commandsIssuedThisTurn } from '../core/botCommands';
import { theme } from './theme';

// =============================================================================
// TYPES
// =============================================================================

export type HoldPreset = 'cover' | 'advance' | 'hold' | 'waypoint';

export interface LeadershipCallbacks {
  onCommand: (botId: number, commandType: BotCommandType, params?: {
    targetPlayerId?: number;
    targetPlanetIndex?: number;
    weaponKey?: WeaponKey;
    holdPreset?: HoldPreset;
    holdTurns?: number;
  }) => void;
  onCancel: (botId: number) => void;
  onDismiss: () => void;
  onToggleHoldFire: (botId: number) => void;
}

// =============================================================================
// VISIBILITY
// =============================================================================

export function showLeadershipPanel(show: boolean): void {
  const panel = document.getElementById('leadership-overlay');
  if (panel) panel.classList.toggle('hidden', !show);
}

export function isLeadershipPanelVisible(): boolean {
  const panel = document.getElementById('leadership-overlay');
  return panel ? !panel.classList.contains('hidden') : false;
}

// =============================================================================
// ORDERS BUTTON
// =============================================================================

export function setupOrdersButton(onToggle: () => void): () => void {
  const btn = document.getElementById('orders-btn');
  if (!btn) return () => {};
  btn.addEventListener('click', onToggle);
  return () => btn.removeEventListener('click', onToggle);
}

export function showOrdersButton(show: boolean): void {
  const btn = document.getElementById('orders-btn');
  if (btn) btn.classList.toggle('hidden', !show);
}

// =============================================================================
// PANEL CONTENT
// =============================================================================

/**
 * Refresh the leadership panel contents.
 * Renders bot teammate cards with command buttons and status.
 */
export function updateLeadershipPanel(
  gameState: GameState,
  playerId: number,
  callbacks: LeadershipCallbacks,
  knownEnemies?: PlayerState[],
  ghosts?: GhostMarker[]
): void {
  const container = document.getElementById('leadership-units');
  if (!container) return;

  const commander = gameState.players.find(p => p.id === playerId);
  if (!commander) return;

  const teammates = getBotTeammates(commander, gameState);
  const canCmd = canIssueCommands(commander, gameState);
  const issuedCount = commandsIssuedThisTurn(playerId, gameState.turnNumber, gameState.botCommands);

  // Update header info
  const costInfo = document.getElementById('leadership-cost-info');
  if (costInfo) {
    costInfo.textContent = `Cost: 0.5 move per order · ${issuedCount}/${3} this turn · Move: ${commander.movementRemaining.toFixed(1)}`;
  }

  container.innerHTML = '';

  for (const bot of teammates) {
    const unit = document.createElement('div');
    unit.className = 'leadership-unit';

    // Header row: unit name, HP, planet
    const header = document.createElement('div');
    header.className = 'leadership-unit-header';
    header.innerHTML = `<span class="leadership-unit-name">${bot.name}</span>` +
      `<span class="leadership-unit-stat">HP ${bot.health}</span>` +
      `<span class="leadership-unit-stat">P${bot.planetIndex}</span>`;
    unit.appendChild(header);

    // Current command status
    const status = document.createElement('div');
    status.className = 'leadership-unit-status';
    if (bot.pendingCommand) {
      const cmd = bot.pendingCommand;
      let desc = '';
      switch (cmd.type) {
        case 'fireAtTarget': {
          const t = gameState.players.find(p => p.id === cmd.targetPlayerId);
          desc = `Fire at ${t?.name ?? 'unknown'}`;
          break;
        }
        case 'fireAtLocation':
          desc = `Fire at Planet ${cmd.targetPlanetIndex}`;
          break;
        case 'moveToPlanet':
          desc = `Move to Planet ${cmd.targetPlanetIndex}`;
          break;
        case 'fireProbe':
          desc = 'Fire scout probe';
          break;
        case 'prioritizeEnemy': {
          const t = gameState.players.find(p => p.id === cmd.targetPlayerId);
          desc = `Prioritize ${t?.name ?? 'unknown'}`;
          break;
        }
        case 'holdPosition':
          desc = `Hold position (${cmd.holdTurns ?? 0} turns left)`;
          break;
      }
      status.textContent = `Current: ${desc}`;
      status.style.color = theme.leadership.amber;
    } else {
      status.textContent = 'Current: Idle';
      status.style.opacity = '0.5';
    }
    unit.appendChild(status);

    // Command buttons row
    const btnRow = document.createElement('div');
    btnRow.className = 'leadership-cmd-row';

    const disabled = !canCmd;

    // FIRE AT button
    const fireBtn = createCmdButton('FIRE AT', disabled, () => {
      showSelectList(unit, 'fire', bot, gameState, callbacks, knownEnemies, ghosts);
    });
    btnRow.appendChild(fireBtn);

    // MOVE TO button
    const moveBtn = createCmdButton('MOVE TO', disabled, () => {
      showSelectList(unit, 'move', bot, gameState, callbacks, knownEnemies);
    });
    btnRow.appendChild(moveBtn);

    // PROBE button
    const hasProbeAmmo = (bot.loadout.ammo.probe === null || (bot.loadout.ammo.probe ?? 0) > 0);
    const probeBtn = createCmdButton('PROBE', disabled || !hasProbeAmmo, () => {
      callbacks.onCommand(bot.id, 'fireProbe');
    });
    btnRow.appendChild(probeBtn);

    // HOLD button (holdPosition with presets)
    const holdBtn = createCmdButton('HOLD', disabled, () => {
      showSelectList(unit, 'hold', bot, gameState, callbacks, knownEnemies, ghosts);
    });
    btnRow.appendChild(holdBtn);

    // PRIORITIZE button
    const prioBtn = createCmdButton('PRIO', disabled, () => {
      showSelectList(unit, 'prioritize', bot, gameState, callbacks, knownEnemies, ghosts);
    });
    btnRow.appendChild(prioBtn);

    // CANCEL button
    const cancelBtn = createCmdButton('CANCEL', !bot.pendingCommand, () => {
      callbacks.onCancel(bot.id);
    });
    cancelBtn.style.borderColor = theme.leadership.cancelBorder;
    cancelBtn.style.color = theme.leadership.cancelText;
    btnRow.appendChild(cancelBtn);

    unit.appendChild(btnRow);

    // HOLD FIRE / OPEN FIRE toggle
    const holdFireBtn = document.createElement('button');
    holdFireBtn.className = 'leadership-cmd-btn';
    holdFireBtn.textContent = bot.holdFire ? 'OPEN FIRE' : 'HOLD FIRE';
    holdFireBtn.style.borderColor = bot.holdFire ? theme.leadership.holdFireBorderActive : theme.surface.border;
    holdFireBtn.style.color = bot.holdFire ? theme.leadership.holdFireTextActive : theme.leadership.amber;
    holdFireBtn.style.width = '100%';
    holdFireBtn.style.marginTop = '4px';
    holdFireBtn.addEventListener('click', () => {
      callbacks.onToggleHoldFire(bot.id);
    });
    unit.appendChild(holdFireBtn);

    container.appendChild(unit);
  }

  // Dismiss button
  const dismissBtn = document.createElement('button');
  dismissBtn.className = 'leadership-dismiss-btn';
  dismissBtn.textContent = 'DISMISS';
  dismissBtn.addEventListener('click', callbacks.onDismiss);
  container.appendChild(dismissBtn);
}

// =============================================================================
// HELPERS
// =============================================================================

function createCmdButton(label: string, disabled: boolean, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'leadership-cmd-btn';
  btn.textContent = label;
  if (disabled) {
    btn.classList.add('disabled');
    btn.style.pointerEvents = 'none';
  } else {
    btn.addEventListener('click', onClick);
  }
  return btn;
}

function showSelectList(
  unitEl: HTMLElement,
  mode: 'fire' | 'move' | 'prioritize' | 'hold',
  bot: PlayerState,
  gameState: GameState,
  callbacks: LeadershipCallbacks,
  knownEnemies?: PlayerState[],
  ghosts?: GhostMarker[]
): void {
  // Remove any existing select list
  const existing = unitEl.querySelector('.leadership-select-list');
  if (existing) { existing.remove(); return; }

  const list = document.createElement('div');
  list.className = 'leadership-select-list';

  if (mode === 'fire' || mode === 'prioritize') {
    // Show known enemies
    const enemies = knownEnemies ?? gameState.players.filter(
      p => p.alive && p.teamIndex !== bot.teamIndex
    );

    if (enemies.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'leadership-select-item';
      empty.textContent = 'No known hostiles';
      empty.style.opacity = '0.5';
      list.appendChild(empty);
    }

    for (const enemy of enemies) {
      const item = document.createElement('div');
      item.className = 'leadership-select-item';
      item.innerHTML = `<span>${enemy.name}</span><span class="leadership-select-stat">HP ${enemy.health} · P${enemy.planetIndex}</span>`;
      item.addEventListener('click', () => {
        const cmdType: BotCommandType = mode === 'fire' ? 'fireAtTarget' : 'prioritizeEnemy';
        callbacks.onCommand(bot.id, cmdType, { targetPlayerId: enemy.id });
        list.remove();
      });
      list.appendChild(item);
    }

    // Also show ghost markers as fire-at-location options
    if (mode === 'fire' && ghosts && ghosts.length > 0) {
      const ghostHeader = document.createElement('div');
      ghostHeader.className = 'leadership-select-item';
      ghostHeader.textContent = '— Ghost intel —';
      ghostHeader.style.opacity = '0.4';
      ghostHeader.style.pointerEvents = 'none';
      list.appendChild(ghostHeader);

      for (const ghost of ghosts) {
        // Find nearest planet to ghost position
        let nearestIdx = 0;
        let nearestDist = Infinity;
        for (let pi = 0; pi < gameState.planets.length; pi++) {
          const p = gameState.planets[pi];
          if (!p.alive) continue;
          const dx = ghost.position.x - p.position.x;
          const dy = ghost.position.y - p.position.y;
          const dz = ghost.position.z - p.position.z;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (dist < nearestDist) { nearestDist = dist; nearestIdx = pi; }
        }
        const ghostPlayer = gameState.players.find(gp => gp.id === ghost.playerId);
        const ghostName = ghostPlayer?.name ?? `Player ${ghost.playerId}`;
        const staleness = gameState.turnNumber - ghost.turnSeen;
        const item = document.createElement('div');
        item.className = 'leadership-select-item';
        item.innerHTML = `<span>${ghostName} near P${nearestIdx}</span><span class="leadership-select-stat">${staleness}t ago</span>`;
        item.style.opacity = '0.7';
        item.addEventListener('click', () => {
          callbacks.onCommand(bot.id, 'fireAtLocation', { targetPlanetIndex: nearestIdx });
          list.remove();
        });
        list.appendChild(item);
      }
    }
  } else if (mode === 'move') {
    // Show alive planets
    for (let i = 0; i < gameState.planets.length; i++) {
      const planet = gameState.planets[i];
      if (!planet.alive || planet.type !== 'planet') continue;
      if (i === bot.planetIndex) continue; // already there

      const knownIds = new Set((knownEnemies ?? []).map(e => e.id));
      const teammates = gameState.players.filter(p => p.alive && p.planetIndex === i && p.teamIndex === bot.teamIndex);
      const visibleEnemies = gameState.players.filter(p => p.alive && p.planetIndex === i && p.teamIndex !== bot.teamIndex && knownIds.has(p.id));
      const parts: string[] = [];
      if (teammates.length > 0) parts.push(teammates.map(p => p.name).join(', '));
      if (visibleEnemies.length > 0) parts.push(visibleEnemies.map(p => p.name).join(', '));
      const occupantText = parts.length > 0 ? parts.join(', ') : 'empty';

      const item = document.createElement('div');
      item.className = 'leadership-select-item';
      item.innerHTML = `<span>Planet ${i}</span><span class="leadership-select-stat">${occupantText}</span>`;
      item.addEventListener('click', () => {
        callbacks.onCommand(bot.id, 'moveToPlanet', { targetPlanetIndex: i });
        list.remove();
      });
      list.appendChild(item);
    }
  } else if (mode === 'hold') {
    // Hold position presets with configurable duration
    const presets: Array<{ label: string; desc: string; preset: HoldPreset }> = [
      { label: 'COVER', desc: 'Move behind planet from enemies', preset: 'cover' },
      { label: 'ADVANCE', desc: 'Move toward known enemies', preset: 'advance' },
      { label: 'DIG IN', desc: 'Stay at current position', preset: 'hold' },
      { label: 'WAYPOINT', desc: 'Click a position on planet', preset: 'waypoint' },
    ];

    for (const p of presets) {
      const item = document.createElement('div');
      item.className = 'leadership-select-item';
      item.innerHTML = `<span>${p.label}</span><span class="leadership-select-stat">${p.desc}</span>`;
      item.addEventListener('click', () => {
        // Show duration sub-selector
        showHoldDurationSelector(list, bot.id, p.preset, callbacks);
      });
      list.appendChild(item);
    }
  }

  unitEl.appendChild(list);
}

function showHoldDurationSelector(
  list: HTMLElement,
  botId: number,
  preset: HoldPreset,
  callbacks: LeadershipCallbacks
): void {
  // Replace list contents with duration options
  list.innerHTML = '';

  const header = document.createElement('div');
  header.className = 'leadership-select-item';
  header.textContent = 'Hold for how many turns?';
  header.style.opacity = '0.5';
  header.style.pointerEvents = 'none';
  list.appendChild(header);

  for (const turns of [1, 2, 3, 5]) {
    const item = document.createElement('div');
    item.className = 'leadership-select-item';
    item.innerHTML = `<span>${turns} turn${turns > 1 ? 's' : ''}</span>`;
    item.addEventListener('click', () => {
      callbacks.onCommand(botId, 'holdPosition', { holdPreset: preset, holdTurns: turns });
      list.remove();
    });
    list.appendChild(item);
  }
}
